/**
 * コンテンツ・レビュー・キャンペーンの状態機械。
 *
 * `mailbox-state.ts` と同じ作法で、**遷移表を唯一の正**にする。
 * ハンドラの中で `if (status === 'draft' && ...)` を書き始めた瞬間に、
 * 「どこで状態が変わるのか」が grep でしか分からなくなり、必ず取りこぼす。
 *
 * ここに置いた 3 つの表が守る不変条件:
 *
 * 1. **承認は人がやる。** `contentApprove` は `TOKEN_FORBIDDEN_PERMISSIONS` に入っており、
 *    さらに `canApproveRevision()` が `principalKind === 'org_token'` を明示的に拒否する。
 *    権限表だけに頼らず二重にするのは、「API から押せる承認は承認ではない」が
 *    この製品の売り（同意証跡の法的強度）の前提だから。
 * 2. **API から大量配信を開始できない。** `schedule` は `scheduled` ではなく
 *    `awaiting_release` に入り、portal の解禁（人間のセッション）を経て初めて `scheduled` になる。
 *    遷移表の `integrationPlane` 列がその判定の唯一の正。
 * 3. **止める操作は広く開ける。** `pause` / `cancel` は統合面からも許す。
 *    止められないことのほうが危険（既存 `updateHalt` を support に許しているのと同じ思想）。
 */
import type { CampaignStatus, ContentReviewState, ContentUploadState } from './enums.js';
import { ORG_PERMISSION, type OrgPermission } from './rbac.js';

/* ============================================== リビジョンのアップロード状態 */

export type ContentUploadEvent = 'commit' | 'fail' | 'abandon';

interface UploadTransition {
  readonly to: ContentUploadState;
  readonly note: string;
}

/**
 * 2 段階アップロード（presign → PUT → commit）の状態遷移。
 *
 * `pending_upload` のまま放置された行を `sweep.content-revisions` が
 * `abandoned` に落とすので、S3 に実体が無い revision が永久に残らない。
 */
export const CONTENT_UPLOAD_TRANSITIONS: Readonly<
  Record<ContentUploadState, Readonly<Partial<Record<ContentUploadEvent, UploadTransition>>>>
> = {
  pending_upload: {
    commit: { to: 'committed', note: 'S3 の実体と sha256 / byte_size が申告と一致した' },
    fail: { to: 'failed', note: 'ハッシュ不一致または実体が存在しない' },
    abandon: { to: 'abandoned', note: '1 時間 commit されなかった（sweep が回収）' },
  },
  committed: {},
  failed: {
    // 同じ revision 番号で再アップロードはさせない。新しい revision を切らせる。
  },
  abandoned: {},
};

export function nextUploadState(from: ContentUploadState, event: ContentUploadEvent): ContentUploadState | null {
  return CONTENT_UPLOAD_TRANSITIONS[from][event]?.to ?? null;
}

/** 本文の実体が S3 に確定しているか。ビルド・プレビュー・配信の前提条件。 */
export function isRevisionBodyAvailable(state: ContentUploadState): boolean {
  return state === 'committed';
}

/* ================================================================ レビュー */

export type ContentReviewEvent =
  | 'submit'
  | 'approve'
  | 'requestChanges'
  | 'withdraw'
  /** 承認不要ポリシーの組織で、下書きからそのまま承認済みへ上げる。 */
  | 'publishWithoutReview';

export interface ContentReviewTransition {
  readonly to: ContentReviewState;
  readonly permission: OrgPermission;
  /**
   * 二名承認（`org_content_policies.two_person_required`）が有効なとき、
   * 依頼者と決裁者が別人であることを要求する。
   */
  readonly distinctActorWhenTwoPerson: boolean;
  /** 組織 API トークンから実行してよいか。**承認系はすべて false**。 */
  readonly integrationPlane: boolean;
  readonly note: string;
}

const O = ORG_PERMISSION;

export const CONTENT_REVIEW_TRANSITIONS: Readonly<
  Record<ContentReviewState, Readonly<Partial<Record<ContentReviewEvent, ContentReviewTransition>>>>
> = {
  draft: {
    submit: {
      to: 'in_review',
      permission: O.contentWrite,
      distinctActorWhenTwoPerson: false,
      integrationPlane: true,
      note: '承認依頼。外部 CMS から依頼まで出せると承認フローを外に持ち出せる',
    },
    publishWithoutReview: {
      to: 'approved',
      permission: O.contentApprove,
      distinctActorWhenTwoPerson: false,
      integrationPlane: false,
      note: 'approval_required=false の組織のみ。権限は承認と同じものを要求する',
    },
  },
  in_review: {
    approve: {
      to: 'approved',
      permission: O.contentApprove,
      distinctActorWhenTwoPerson: true,
      integrationPlane: false,
      note: '承認は「人が読んで責任を取った」記録。トークンから押せてはならない',
    },
    requestChanges: {
      to: 'changes_requested',
      permission: O.contentApprove,
      distinctActorWhenTwoPerson: true,
      integrationPlane: false,
      note: '差し戻し',
    },
    withdraw: {
      to: 'draft',
      permission: O.contentWrite,
      distinctActorWhenTwoPerson: false,
      integrationPlane: true,
      note: '依頼の取り下げ。責任を負う操作ではないので書き込み権限で足りる',
    },
  },
  changes_requested: {
    submit: {
      to: 'in_review',
      permission: O.contentWrite,
      distinctActorWhenTwoPerson: false,
      integrationPlane: true,
      note: '修正後の再依頼',
    },
  },
  // 承認済みリビジョンは凍結する。編集は新しい revision を切る（CD §5.2）。
  approved: {},
};

export function contentReviewTransition(
  from: ContentReviewState,
  event: ContentReviewEvent,
): ContentReviewTransition | null {
  return CONTENT_REVIEW_TRANSITIONS[from][event] ?? null;
}

/** 承認済みリビジョンは本文を書き換えられない。 */
export function isRevisionEditable(reviewState: ContentReviewState, frozenAt: Date | null): boolean {
  if (frozenAt !== null) return false;
  return reviewState !== 'approved';
}

export interface ContentReviewPolicy {
  /** `org_content_policies.approval_required` */
  readonly approvalRequired: boolean;
  /** `org_content_policies.two_person_required` */
  readonly twoPersonRequired: boolean;
}

export interface ReviewActor {
  /** `org_user` か `org_token` か。**トークンは承認できない**。 */
  readonly kind: 'org_user' | 'org_token' | 'staff';
  readonly orgUserId: string | null;
  readonly permissions: ReadonlySet<OrgPermission>;
}

export type ReviewDenyReason =
  | 'TRANSITION_NOT_ALLOWED'
  | 'PERMISSION_MISSING'
  | 'TOKEN_NOT_ALLOWED'
  | 'SELF_APPROVAL'
  | 'APPROVAL_NOT_REQUIRED'
  | 'APPROVAL_REQUIRED';

export interface ReviewDecision {
  readonly ok: boolean;
  readonly to: ContentReviewState | null;
  readonly reason: ReviewDenyReason | null;
}

/**
 * レビューの遷移を判定する。**API のハンドラはこの関数だけを見る**。
 *
 * @param requestedByOrgUserId 承認依頼を出した契約者ユーザ（二名承認の突合に使う）
 */
export function evaluateReviewTransition(params: {
  from: ContentReviewState;
  event: ContentReviewEvent;
  actor: ReviewActor;
  policy: ContentReviewPolicy;
  requestedByOrgUserId?: string | null;
}): ReviewDecision {
  const { from, event, actor, policy } = params;
  const transition = contentReviewTransition(from, event);
  if (!transition) return { ok: false, to: null, reason: 'TRANSITION_NOT_ALLOWED' };

  if (actor.kind === 'org_token' && !transition.integrationPlane) {
    return { ok: false, to: null, reason: 'TOKEN_NOT_ALLOWED' };
  }
  if (!actor.permissions.has(transition.permission)) {
    return { ok: false, to: null, reason: 'PERMISSION_MISSING' };
  }

  if (event === 'publishWithoutReview' && policy.approvalRequired) {
    return { ok: false, to: null, reason: 'APPROVAL_REQUIRED' };
  }
  if (event === 'submit' && !policy.approvalRequired) {
    // 承認不要の組織で承認フローを回し始めると、承認されないまま滞留する。
    return { ok: false, to: null, reason: 'APPROVAL_NOT_REQUIRED' };
  }

  if (
    transition.distinctActorWhenTwoPerson &&
    policy.twoPersonRequired &&
    params.requestedByOrgUserId &&
    actor.orgUserId &&
    params.requestedByOrgUserId === actor.orgUserId
  ) {
    return { ok: false, to: null, reason: 'SELF_APPROVAL' };
  }

  return { ok: true, to: transition.to, reason: null };
}

export const REVIEW_DENY_MESSAGE_JA: Readonly<Record<ReviewDenyReason, string>> = {
  TRANSITION_NOT_ALLOWED: '現在の状態からこの操作は行えません',
  PERMISSION_MISSING: 'この操作を行う権限がありません',
  TOKEN_NOT_ALLOWED: 'API トークンからは承認できません。管理画面から操作してください',
  SELF_APPROVAL: '二名承認が有効なため、承認を依頼した本人は承認できません',
  APPROVAL_NOT_REQUIRED: 'この組織では承認フローが無効です',
  APPROVAL_REQUIRED: 'この組織では承認が必須です。承認依頼を出してください',
};

/* ============================================================ キャンペーン */

export type CampaignEvent =
  | 'validate'
  | 'invalidate'
  | 'testSend'
  | 'schedule'
  | 'release'
  | 'releaseExpire'
  | 'start'
  | 'pause'
  | 'resume'
  | 'complete'
  | 'cancel'
  | 'halt'
  | 'fail';

export interface CampaignTransition {
  readonly to: CampaignStatus;
  readonly permission: OrgPermission;
  /**
   * 組織 API トークン（統合面）から実行してよいか。
   *
   * **`start` と `resume` は false 固定。** 10 万通は撤回できないので、
   * 「盗まれたトークン 1 本の被害が事業ごと焼く操作」に該当する。
   * 一方 `pause` / `cancel` は true。止める操作を塞ぐほうが危険。
   */
  readonly integrationPlane: boolean;
  readonly note: string;
}

/**
 * キャンペーンの遷移表。
 *
 * `awaiting_release` は本設計で新設した状態で、
 * **API がスケジュールを入れられるが、人が解禁するまで送らない**ための踊り場。
 */
export const CAMPAIGN_TRANSITIONS: Readonly<
  Record<CampaignStatus, Readonly<Partial<Record<CampaignEvent, CampaignTransition>>>>
> = {
  draft: {
    validate: { to: 'validated', permission: O.campaignWrite, integrationPlane: true, note: 'F5 プリフライト合格' },
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
  },
  validated: {
    testSend: { to: 'test_sent', permission: O.campaignWrite, integrationPlane: true, note: '自組織宛のテスト送信' },
    invalidate: {
      to: 'draft',
      permission: O.campaignWrite,
      integrationPlane: true,
      note: 'コンテンツ差し替えでトリガが自動的に戻す（t85_campaign_content_invalidate）',
    },
    schedule: {
      to: 'awaiting_release',
      permission: O.campaignWrite,
      integrationPlane: true,
      note: '**scheduled ではない**。人の解禁を待つ',
    },
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
  },
  test_sent: {
    invalidate: { to: 'draft', permission: O.campaignWrite, integrationPlane: true, note: 'コンテンツ差し替え' },
    schedule: { to: 'awaiting_release', permission: O.campaignWrite, integrationPlane: true, note: '' },
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
  },
  awaiting_release: {
    release: {
      to: 'scheduled',
      permission: O.campaignSend,
      integrationPlane: false,
      note: 'portal の解禁ボタンのみ。campaignSend は TOKEN_FORBIDDEN',
    },
    releaseExpire: {
      to: 'canceled',
      permission: O.campaignWrite,
      integrationPlane: false,
      note: '解禁されないまま scheduled_at を過ぎた。忘れた頃に飛ぶ事故を防ぐ',
    },
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
  },
  scheduled: {
    start: {
      to: 'sending',
      permission: O.campaignSend,
      integrationPlane: false,
      note: 'スケジューラ（system アクター）が起こす。API には出さない',
    },
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
    halt: { to: 'halted', permission: O.campaignHalt, integrationPlane: true, note: '' },
  },
  sending: {
    pause: { to: 'paused', permission: O.campaignHalt, integrationPlane: true, note: '止める側は広く開ける' },
    complete: { to: 'completed', permission: O.campaignSend, integrationPlane: false, note: 'ワーカーが遷移させる' },
    halt: { to: 'halted', permission: O.campaignHalt, integrationPlane: true, note: '運営の kill-switch も同じ口' },
    fail: { to: 'failed', permission: O.campaignSend, integrationPlane: false, note: '' },
  },
  paused: {
    resume: {
      to: 'sending',
      permission: O.campaignSend,
      integrationPlane: false,
      note: '**再開も人間のセッションのみ**。止めるのは広く、動かすのは狭く',
    },
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
    halt: { to: 'halted', permission: O.campaignHalt, integrationPlane: true, note: '' },
  },
  halted: {
    cancel: { to: 'canceled', permission: O.campaignWrite, integrationPlane: true, note: '' },
  },
  completed: {},
  canceled: {},
  failed: {},
};

export function campaignTransition(from: CampaignStatus, event: CampaignEvent): CampaignTransition | null {
  return CAMPAIGN_TRANSITIONS[from][event] ?? null;
}

/** その状態からもう本文・宛先・ストリームを変更できないか（CD §5.2 の不変条件）。 */
export function isCampaignContentFrozen(status: CampaignStatus): boolean {
  return status !== 'draft' && status !== 'validated' && status !== 'test_sent';
}

/** 統合面（組織 API トークン）から起こしてよいイベントか。 */
export function isCampaignEventAllowedFromIntegration(from: CampaignStatus, event: CampaignEvent): boolean {
  return campaignTransition(from, event)?.integrationPlane === true;
}
